import React, { useState } from "react";
import { WarningCircle, Info } from "@phosphor-icons/react";

/**
 * Iter 19.68 — Soft-Warning-Badges für die Quality-Validation-Schicht.
 *
 * Rendert für jedes Vordienst-Item bis zu zwei kleine Indikator-Symbole:
 *   • Orangenes ⚠️  bei einem oder mehreren `_quality_flags`
 *     (Hallucination-Check, Folgezeilen-Übernahme, Adjazenz-Heuristik, …)
 *   • Blaues ℹ️  bei vorhandener `_auto_corrected`-Audit-Spur
 *     (Original-Datum wurde vorsichtig durch OCR-belegte Alternative
 *     überschrieben — Sachbearbeiter soll sehen, dass ET aktiv eingegriffen
 *     hat, ohne es zu erschrecken).
 *
 * Beide Symbole zeigen beim Klick eine kleine Detail-Box mit:
 *   • Typ-Label
 *   • Klartext-Erklärung
 *   • Bei Auto-Korrektur: from → to (Audit-Trail für DSGVO-Nachweis)
 *
 * Iter 19.68.3 — Erweitert um eine „Position geprüft & bestätigt"-Checkbox
 * in der Detail-Box. Wenn der User aktiv bestätigt (oder die Row manuell
 * bearbeitet — `_quality_ack` wird im VordienstRow-Wrapper gesetzt),
 * verschwinden Badge + Border-Stripe + Hinweisbox-Eintrag.
 */
const TYPE_LABEL = {
  hallucination: "Datum nicht im Quelltext gefunden",
  folgezeilen_uebernahme: "Möglicher Layout-Bruch-Fehler",
  dd_mm_swap: "Tag/Monat-Vertauschung",
  jahr_shift: "Jahresangabe korrigiert",
  jahres_verkuerzung: "Jahres-Anpassung",
};

const CORRECTION_REASON_LABEL = {
  dd_mm_swap: "Tag und Monat vertauscht — automatisch korrigiert",
  jahr_shift: "Jahreszahl korrigiert",
};

export default function QualityBadges({ row, onAckToggle }) {
  const [open, setOpen] = useState(null);
  const flags = Array.isArray(row._quality_flags) ? row._quality_flags : [];
  const corrections = Array.isArray(row._auto_corrected) ? row._auto_corrected : [];
  const acked = row._quality_ack === true;
  const hasFlags = flags.length > 0 && !acked;
  const hasCorrections = corrections.length > 0;
  if (!hasFlags && !hasCorrections) return null;

  return (
    <div
      data-testid={`vd-quality-badges-${row.__index ?? ""}`}
      className="mt-1 flex flex-wrap items-center gap-1.5"
    >
      {hasFlags && (
        <button
          type="button"
          data-testid="vd-quality-flag-badge"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(open === "flags" ? null : "flags");
          }}
          className={[
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10.5px] font-medium uppercase tracking-wider",
            "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 transition-crisp",
          ].join(" ")}
          title="Datums-Validierung schlägt Alarm — bitte Quelldokument prüfen"
        >
          <WarningCircle size={12} weight="fill" />
          Bitte prüfen
        </button>
      )}
      {hasCorrections && (
        <button
          type="button"
          data-testid="vd-quality-corrected-badge"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(open === "corrections" ? null : "corrections");
          }}
          className={[
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10.5px] font-medium uppercase tracking-wider",
            "bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100 transition-crisp",
          ].join(" ")}
          title="ET hat eine Datumsangabe automatisch gegen den Quelltext abgeglichen"
        >
          <Info size={12} weight="fill" />
          Auto-Korrektur
        </button>
      )}
      {open === "flags" && hasFlags && (
        <div
          data-testid="vd-quality-flag-detail"
          className="basis-full mt-1 text-[11.5px] leading-snug bg-amber-50/70 border border-amber-300 rounded-sm p-2 text-amber-900"
        >
          <ul className="space-y-1 list-disc pl-4">
            {flags.map((f, i) => (
              <li key={i}>
                <strong>{TYPE_LABEL[f.type] || f.type}:</strong>{" "}
                {f.message || ""}
              </li>
            ))}
          </ul>
          {/* Iter 19.68.3 — Explizite Bestätigung. Wenn User aktiv prüft
              UND alles passt, kann er hier zustimmen → Banner-Eintrag,
              Border-Stripe und Badge verschwinden. */}
          {typeof onAckToggle === "function" && (
            <label
              className="mt-2 flex items-center gap-1.5 text-[11.5px] font-medium text-amber-900 cursor-pointer select-none"
              data-testid="vd-quality-ack-label"
            >
              <input
                type="checkbox"
                checked={row._quality_ack === true}
                onChange={(e) => onAckToggle(e.target.checked)}
                data-testid="vd-quality-ack-checkbox"
                className="accent-amber-600 w-3.5 h-3.5"
              />
              <span>Position geprüft &amp; bestätigt</span>
            </label>
          )}
        </div>
      )}
      {open === "corrections" && (
        <div
          data-testid="vd-quality-corrected-detail"
          className="basis-full mt-1 text-[11.5px] leading-snug bg-sky-50/70 border border-sky-300 rounded-sm p-2 text-sky-900"
        >
          <ul className="space-y-1 list-disc pl-4">
            {corrections.map((c, i) => (
              <li key={i}>
                <strong>{CORRECTION_REASON_LABEL[c.reason] || c.reason}:</strong>{" "}
                {c.field === "datum_bis" ? "Bis-Datum" : "Von-Datum"} ursprünglich{" "}
                <code className="bg-white/60 px-1 rounded text-[10.5px]">
                  {formatIsoToDdMmYyyy(c.from)}
                </code>{" "}
                → korrigiert auf{" "}
                <code className="bg-white/60 px-1 rounded text-[10.5px]">
                  {formatIsoToDdMmYyyy(c.to)}
                </code>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatIsoToDdMmYyyy(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 10) return iso || "";
  const y = iso.slice(0, 4);
  const m = iso.slice(5, 7);
  const d = iso.slice(8, 10);
  return `${d}.${m}.${y}`;
}
